/**
 * Module dependencies.
 */

import { EmailInput, Form, SubscribeButton } from './newsletter-form.style';
import { FooterSection, FooterTitle, Paragraph } from '../footer/footer.style';
import { JSX, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'next-i18next';
import toast from 'react-hot-toast';

/**
 * `NewsletterFormData` type.
 */

type NewsletterFormData = {
  email: string;
};

/**
 * Export `NewsletterForm` component.
 */

export function NewsletterForm(): JSX.Element {
  const [translate] = useTranslation();
  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
  } = useForm<NewsletterFormData>();

  const submitData = useCallback(
    async (data: NewsletterFormData) => {
      const response = await fetch('/api/newsletter', {
        body: JSON.stringify(data),
        headers: { 'Content-Type': 'application/json' },
        method: 'POST',
      });

      if (!response.ok) throw new Error('Failed to subscribe');

      reset();
    },
    [reset],
  );

  const onSubmit = useCallback(
    (data) => {
      toast.promise(submitData(data), {
        error: translate('contacts.form.toast.error'),
        loading: translate('contacts.form.toast.loading'),
        success: translate('contacts.form.toast.success'),
      });
    },
    [submitData, translate],
  );

  return (
    <FooterSection>
      <FooterTitle>{translate('footer.newsletter')}</FooterTitle>

      <Paragraph>{translate('footer.newsletterDescription')}</Paragraph>

      <Form onSubmit={handleSubmit(onSubmit)}>
        <EmailInput
          placeholder={translate('footer.emailPlaceholder')}
          size={'small'}
          variant={'outlined'}
          {...register('email', {
            pattern: {
              message: translate('errors.emailInvalid'),
              value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
            },
            required: translate('errors.emailRequired'),
          })}
          error={!!errors.email}
          helperText={errors.email?.message}
        />

        <SubscribeButton
          color={'primary'}
          type={'submit'}
          variant={'contained'}
        >
          {translate('actions.subscribe')}
        </SubscribeButton>
      </Form>
    </FooterSection>
  );
}
